import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { navLinks } from "@/mocks/site";

const today = new Date();
const dateText = `${today.getFullYear()} 年 ${today.getMonth() + 1} 月 ${today.getDate()} 日`;

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/?q=${encodeURIComponent(q)}` : "/");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled ? "bg-background-50/95 backdrop-blur border-background-200" : "bg-background-50 border-background-100"
      }`}
    >
      {/* 顶部信息条 */}
      <div className="hidden md:block border-b border-background-100 bg-background-100">
        <div className="w-full px-6 py-1.5 flex items-center justify-between text-xs text-foreground-600">
          <p className="flex items-center gap-2">
            <i className="ri-newspaper-line" aria-hidden="true" />
            <span>{dateText} · 编辑部滚动跟进中</span>
          </p>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <i className="ri-mail-line" aria-hidden="true" />
              tips@wangbaochigua.example
            </span>
            <Link to="/about" className="hover:text-primary-600 transition-colors">
              内容原则
            </Link>
          </div>
        </div>
      </div>

      {/* 品牌与导航 */}
      <div className="w-full px-4 md:px-6">
        <div className="h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              aria-label="打开导航菜单"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} aria-hidden="true" />
            </button>

            <Link to="/" className="flex items-baseline gap-2 min-w-0">
              <span className="font-brand text-3xl md:text-4xl text-primary-600 leading-none">网曝吃瓜</span>
              <span className="hidden sm:inline text-xs text-foreground-500 whitespace-nowrap">网友爆料投稿聚集地</span>
            </Link>
          </div>

          <nav aria-label="主导航" className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-md whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-primary-600 bg-primary-50 font-medium"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={submitSearch} className="hidden md:flex items-center gap-2" role="search">
              <label htmlFor="header-search" className="sr-only">
                站内搜索
              </label>
              <div className="relative">
                <i
                  className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
                  aria-hidden="true"
                />
                <input
                  id="header-search"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索线索、栏目或关键词"
                  className="w-40 lg:w-52 h-9 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                />
              </div>
            </form>

            <Link
              to="/submit"
              className="inline-flex items-center gap-1.5 h-9 px-3 md:px-4 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-send-plane-fill" aria-hidden="true" />
              <span className="hidden sm:inline">我要投稿</span>
            </Link>
          </div>
        </div>
      </div>

      {/* 移动端菜单 */}
      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-3 soft-fade">
          <form onSubmit={submitSearch} className="mb-3" role="search">
            <div className="relative">
              <i
                className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
                aria-hidden="true"
              />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索线索、栏目或关键词"
                className="w-full h-10 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          </form>
          <nav aria-label="移动端导航" className="grid grid-cols-2 gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `px-3 py-2.5 text-sm rounded-md transition-colors ${
                    isActive ? "bg-primary-50 text-primary-600 font-medium" : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <NavLink
              to="/about"
              className="px-3 py-2.5 text-sm rounded-md text-foreground-700 hover:bg-background-100 transition-colors"
            >
              关于我们
            </NavLink>
            <NavLink
              to="/submit"
              className="px-3 py-2.5 text-sm rounded-md text-foreground-700 hover:bg-background-100 transition-colors"
            >
              我要投稿
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}